import 'highlight.js/styles/atom-one-dark.css';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { AutoLogout } from '@backstage/core-components';
import {
  AlertDisplay,
  OAuthRequestDialog,
  ProxiedSignInPage,
  SignInPage,
} from '@backstage/core-components';
import { Header } from '@backstage/core-components';
import {
  configApiRef,
  createRoutableExtension,
  githubAuthApiRef,
  microsoftAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { ApiExplorerPage, apiDocsPlugin } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { HomepageCompositionRoot } from '@backstage/plugin-home';
import { LighthousePage } from '@backstage/plugin-lighthouse';
import { orgPlugin } from '@backstage/plugin-org';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { SearchPage as BackstageSearchPage } from '@backstage/plugin-search';
import { TechRadarPage } from '@backstage/plugin-tech-radar';
import {
  TechDocsIndexPage,
  TechDocsReaderPage,
  techdocsPlugin,
} from '@backstage/plugin-techdocs';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import {
  userSettingsPlugin,
  UserSettingsPage,
} from '@backstage/plugin-user-settings';
import { UnifiedThemeProvider } from '@backstage/theme';
import GitHubIcon from '@mui/icons-material/GitHub';
import CloudQueueIcon from '@material-ui/icons/CloudQueue';
import { RagModal } from '@roadiehq/rag-ai';
import React from 'react';
import { Navigate, Route, useLocation } from 'react-router-dom';
import { apis } from './apis';
// import FloatingChatButton from './components/FloatingChatButton/FloatingChatButton';
import { NoraPage } from './components/nora/NoraPage';
import { ArgoCdPage } from './components/argocd/ArgoCdPage';
import { OpenBaoPage } from './components/openbao/OpenBaoPage';
import {
  KubernetesPage,
  KubernetesResourcePage,
} from './components/kubernetes/KubernetesPage';
import { Root } from './components/Root';
import { SourcebotTestComponent } from './components/SourcebotTestComponent';
import { SourcebotChatComponent } from './components/SourcebotChatComponent';
import { SourcebotPage } from './components/SourcebotPage';
import { entityPage } from './components/catalog/EntityPage';
import { CustomCatalogPage } from './components/catalog/CustomCatalogPage';

// Wrap CustomCatalogPage as a routable extension so catalogPlugin.routes.catalogIndex
// gets a path bound to it (prevents "No path for routeRef{catalog-index}" console error).
const CatalogIndexPageBound = catalogPlugin.provide(
  createRoutableExtension({
    name: 'CatalogIndexPage',
    component: () => Promise.resolve(CustomCatalogPage),
    mountPoint: catalogPlugin.routes.catalogIndex,
  }),
);

import { HomePage } from './components/home/HomePage';
import { SearchPage } from './components/search/SearchPage';
import { TasksPage } from './components/TasksPage';
import { TaskDetailPage } from './components/TaskDetailPage';
import { CustomScaffolderTaskPage } from './components/scaffolder/CustomScaffolderTaskPage';
import { EntraGroupPickerFieldExtension } from './components/scaffolder/EntraGroupPicker';
import { ScaffolderFieldExtensions } from '@backstage/plugin-scaffolder-react';

import DarkIcon from '@material-ui/icons/Brightness3';
import { openshiftConsoleTheme } from './themes/openshiftConsoleTheme';

function LegacyChatRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/nora${search}`} replace />;
}

const app = createApp({
  apis,
  themes: [
    {
      id: 'openshift-console',
      title: 'Platform Console',
      variant: 'dark',
      icon: <DarkIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider
          theme={openshiftConsoleTheme}
          children={children}
        />
      ),
    },
  ],

  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      // viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
    bind(userSettingsPlugin.externalRoutes, {});
  },
  components: {
    SignInPage: props => {
      const configApi = useApi(configApiRef);

      if (configApi.getString('auth.environment') === 'local') {
        return (
          <SignInPage
            {...props}
            title="Local Development"
            align="center"
            providers={[
              {
                id: 'github',
                title: 'GitHub',
                message: 'Sign in using your GitHub account.',
                apiRef: githubAuthApiRef,
              },
              {
                id: 'microsoft-auth-provider',
                title: 'Azure AD',
                message: 'Sign in with your PostNord Azure AD account',
                apiRef: microsoftAuthApiRef,
              },
            ]}
          />
        );
        // biome-ignore lint/style/noUselessElse: <explanation>
      } else if (configApi.getString('auth.environment') === 'development') {
        return (
          <SignInPage
            {...props}
            title="Select a sign-in method"
            align="center"
            providers={[
              {
                id: 'microsoft-auth-provider',
                title: 'Azure AD',
                message: 'Sign in with your PostNord Azure AD account',
                apiRef: microsoftAuthApiRef,
              },
            ]}
          />
        );
      }
      return <ProxiedSignInPage {...props} provider="oauth2Proxy" />;
    },
  },
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<HomepageCompositionRoot />}>
      <HomePage />
    </Route>
    <Route path="/catalog" element={<CatalogIndexPageBound />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route
      path="/create"
      element={
        <ScaffolderPage
          headerOptions={{ title: 'Golden Path Templates' }}
          components={{ TaskPageComponent: CustomScaffolderTaskPage }}
        />
      }
    >
      <ScaffolderFieldExtensions>
        <EntraGroupPickerFieldExtension />
      </ScaffolderFieldExtensions>
    </Route>
    <Route
      path="/access-control"
      element={
        <>
          <Header title="Access Control" />
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              padding: '40px',
            }}
          >
            <div
              style={{
                width: '90%',
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '30px',
              }}
            >
              {/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
              <div
                onClick={() =>
                  // biome-ignore lint/suspicious/noAssignInExpressions: <explanation>
                  (window.location.href =
                    'https://backstage.ess.postnord.com/create/templates/default/github-onboarding')
                }
                style={{
                  padding: '40px',
                  backgroundColor: '#dcdcdc',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '20px',
                  transition: 'transform 0.2s ease-in-out',
                  height: '200px',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.backgroundColor = '#c0c0c0';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.backgroundColor = '#dcdcdc';
                }}
              >
                <GitHubIcon style={{ fontSize: '3rem' }} />
                <span style={{ fontSize: '1.5rem', textAlign: 'center' }}>
                  GitHub Onboarding
                </span>
              </div>
              {/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
              <div
                onClick={() =>
                  // biome-ignore lint/suspicious/noAssignInExpressions: <explanation>
                  (window.location.href =
                    'https://backstage.ess.postnord.com/create/templates/default/grafana-cloud-onboarding')
                }
                style={{
                  padding: '40px',
                  backgroundColor: '#dcdcdc',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '20px',
                  transition: 'transform 0.2s ease-in-out',
                  height: '200px',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.backgroundColor = '#c0c0c0';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.backgroundColor = '#dcdcdc';
                }}
              >
                <CloudQueueIcon style={{ fontSize: '3rem' }} />
                <span style={{ fontSize: '1.5rem', textAlign: 'center' }}>
                  Grafana Cloud Onboarding
                </span>
              </div>
              {[...Array(6)].map((_, index) => (
                <div
                  key={index}
                  style={{
                    height: '200px',
                  }}
                />
              ))}
            </div>
          </div>
        </>
      }
    />{' '}
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/tech-radar"
      element={
        <TechRadarPage
          width={1500}
          height={800}
          id="default"
          title="PostNord Tech Radar"
          subtitle="Technology Adoption Radar"
          pageTitle="PostNord Tech Radar (To be classified further)"
        />
      }
    />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<BackstageSearchPage />}>
      <SearchPage />
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/chat" element={<LegacyChatRedirect />} />
    <Route path="/nora" element={<NoraPage />} />
    <Route path="/argocd" element={<ArgoCdPage />} />
    <Route path="/openbao" element={<OpenBaoPage />} />
    <Route path="/kubernetes" element={<KubernetesPage />} />
    {/* Full-window resource pages, shaped after the OpenShift console:
        /kubernetes/c/<cluster>/ns/<namespace>/<kind>/<name>/<tab>
        /kubernetes/c/<cluster>/cluster/<kind>/<name>/<tab>          */}
    <Route
      path="/kubernetes/c/:cluster/ns/:namespace/:kindId/:name"
      element={<KubernetesResourcePage />}
    />
    <Route
      path="/kubernetes/c/:cluster/ns/:namespace/:kindId/:name/:tab"
      element={<KubernetesResourcePage />}
    />
    <Route
      path="/kubernetes/c/:cluster/cluster/:kindId/:name"
      element={<KubernetesResourcePage />}
    />
    <Route
      path="/kubernetes/c/:cluster/cluster/:kindId/:name/:tab"
      element={<KubernetesResourcePage />}
    />
    <Route path="/sourcebot" element={<SourcebotPage />} />
    <Route path="/sourcebot-test" element={<SourcebotTestComponent />} />
    <Route path="/sourcebot-chat" element={<SourcebotChatComponent />} />
    <Route path="/lighthouse" element={<LighthousePage />} />
    <Route path="/tasks" element={<TasksPage />} />
    <Route path="/tasks/:taskId" element={<TaskDetailPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <AutoLogout />
    <AppRouter>
      <RagModal />
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
